import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { store } from '../../store/store';
import {
  DocumentTextIcon,
  PencilIcon,
  EyeIcon,
  PlusIcon,
  BarsArrowUpIcon,
  PrinterIcon,
  ShareIcon,
  CheckIcon,
  AdjustmentsHorizontalIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  DocumentDuplicateIcon,
  TrashIcon,
  SparklesIcon,
  MagnifyingGlassPlusIcon,
  BoltIcon,
  AcademicCapIcon,
  BuildingStorefrontIcon,
  CurrencyDollarIcon,
  ClipboardDocumentIcon,
  ArrowPathIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import {
  CheckCircleIcon as CheckCircleSolidIcon,
  XCircleIcon as XCircleSolidIcon,
  ClockIcon as ClockSolidIcon,
  StarIcon as StarSolidIcon,
  DocumentTextIcon as DocumentTextSolidIcon,
  RocketLaunchIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  SwatchIcon,
  CpuChipIcon,
  CloudArrowUpIcon,
  PresentationChartLineIcon
} from '@heroicons/react/24/solid';
import {
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { Menu } from '@headlessui/react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  useGetLetterTemplatesQuery,
  useCloneLetterTemplateMutation,
  useUpdateLetterTemplateMutation,
  useGetGeneratedLettersQuery,
  useGenerateLetterMutation,
  useDeleteGeneratedLetterMutation
} from '../../services/letterApi';
import { useGetEmployeesQuery } from '../../services/employeeApi';
import { Button, Modal, Input } from '../../components/common';
import toast from 'react-hot-toast';

const LetterManagement = () => {
  const { t } = useTranslation(['common', 'letters']);
  const navigate = useNavigate();

  // Get user for permissions
  const user = useSelector((state) => state.auth.user);

  // Check if user is employee
  const isEmployee = user?.role === 'employee';

  // Main tab state - default to letters for employees, templates for admins
  const [activeTab, setActiveTab] = useState(isEmployee ? 'letters' : 'templates'); // 'templates', 'letters'
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showLetterActionsModal, setShowLetterActionsModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [cloneName, setCloneName] = useState('');
  const [previewContent, setPreviewContent] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [letterPurpose, setLetterPurpose] = useState('');
  const [letterNotes, setLetterNotes] = useState('');

  const [generatingLetter, setGeneratingLetter] = useState(false);

  // Employee search states - following asset allocation pattern
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [debouncedEmployeeSearch, setDebouncedEmployeeSearch] = useState('');

  // Letters tab filters and search
  const [lettersSearchQuery, setLettersSearchQuery] = useState('');
  const [lettersEmployeeFilter, setLettersEmployeeFilter] = useState('');
  const [lettersCategoryFilter, setLettersCategoryFilter] = useState('');

  // Sample employees for demonstration
  const sampleEmployees = [
    { id: 'emp001', firstName: 'John', lastName: 'Doe', employeeId: 'EMP001', position: 'Senior Developer', department: 'Engineering', status: 'active' },
    { id: 'emp002', firstName: 'Jane', lastName: 'Smith', employeeId: 'EMP002', position: 'HR Manager', department: 'Human Resources', status: 'active' },
    { id: 'emp003', firstName: 'Bob', lastName: 'Johnson', employeeId: 'EMP003', position: 'UI Designer', department: 'Design', status: 'active' },
    { id: 'emp004', firstName: 'Alice', lastName: 'Brown', employeeId: 'EMP004', position: 'Project Manager', department: 'Operations', status: 'active' },
    { id: 'emp005', firstName: 'Charlie', lastName: 'Wilson', employeeId: 'EMP005', position: 'Data Analyst', department: 'Business Intelligence', status: 'active' },
    { id: 'emp006', firstName: 'Diana', lastName: 'Garcia', employeeId: 'EMP006', position: 'Marketing Specialist', department: 'Marketing', status: 'active' },
    { id: 'emp007', firstName: 'Edward', lastName: 'Miller', employeeId: 'EMP007', position: 'DevOps Engineer', department: 'Engineering', status: 'active' },
    { id: 'emp008', firstName: 'Fiona', lastName: 'Davis', employeeId: 'EMP008', position: 'Financial Analyst', department: 'Finance', status: 'active' }
  ];

  // Sample data for demonstration
  const sampleTemplates = [
    {
      _id: 'sample-offer',
      name: 'Offer Letter Template',
      description: 'Professional job offer letter template with company branding',
      category: 'offer',
      status: 'active',
      isDefault: true,
      usageCount: 25,
      htmlContent: '<div><h1>Sample Offer Letter</h1><p>{{employee.firstName}}, welcome to {{company.name}}!</p></div>',
      placeholders: ['employee.firstName', 'company.name']
    },
    {
      _id: 'sample-appointment',
      name: 'Appointment Letter Template',
      description: 'Official appointment confirmation letter',
      category: 'appointment',
      status: 'draft',
      isDefault: false,
      usageCount: 18,
      htmlContent: '<div><h1>Appointment Letter</h1><p>{{employee.firstName}}, you are appointed as {{employee.position}} in {{employee.department}}.</p></div>',
      placeholders: ['employee.firstName', 'employee.position', 'employee.department']
    },
    {
      _id: 'sample-experience',
      name: 'Experience Certificate Template',
      description: 'Official experience certificate for employment verification',
      category: 'experience',
      status: 'inactive',
      isDefault: false,
      usageCount: 12,
      htmlContent: '<div><h1>Experience Certificate</h1><p>{{employee.firstName}} worked as {{employee.position}} in {{employee.department}} at {{company.name}}.</p></div>',
      placeholders: ['employee.firstName', 'employee.position', 'employee.department', 'company.name']
    },
    {
      _id: 'sample-promotion',
      name: 'Promotion Letter Template',
      description: 'Congratulations letter for employee promotions',
      category: 'promotion',
      status: 'draft',
      isDefault: false,
      usageCount: 8,
      htmlContent: '<div><h1>Promotion Letter</h1><p>Congratulations {{employee.firstName}} on your promotion to {{employee.position}} in {{employee.department}}!</p></div>',
      placeholders: ['employee.firstName', 'employee.position', 'employee.department']
    }
  ];

  // Mock API responses for demonstration
  const mockTemplatesData = {
    data: sampleTemplates,
    pagination: {
      total: sampleTemplates.length,
      page: 1,
      pages: 1
    }
  };

  const mockLettersData = {
    data: [],
    stats: {
      totalLetters: 0,
      totalTemplates: sampleTemplates.length
    }
  };

  // API hooks
  const { data: templatesData, isLoading: templatesLoading, refetch: refetchTemplates } = useGetLetterTemplatesQuery({
    status: activeTab === 'templates' ? undefined : 'active',
    category: categoryFilter || undefined,
    search: searchQuery || undefined
  });

  const { data: lettersData, isLoading: lettersLoading } = useGetGeneratedLettersQuery({});

  // Use real data if available, otherwise use sample data
  const actualTemplatesData = templatesData && templatesData.data && templatesData.data.length > 0 ? templatesData : [];
  const actualLettersData = lettersData?.data && lettersData.data.length > 0 ? lettersData : [];

  const [cloneTemplate] = useCloneLetterTemplateMutation();
  const [updateLetterTemplate] = useUpdateLetterTemplateMutation();
  const [generateLetter, { isLoading: generating }] = useGenerateLetterMutation();
  const [deleteGeneratedLetter, { isLoading: deleting }] = useDeleteGeneratedLetterMutation();

  // Debounce employee search - following asset allocation pattern
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedEmployeeSearch(employeeSearchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [employeeSearchTerm]);

  // Employee API hook for the generate letter modal - following asset allocation pattern (no branch filter since letters are company-wide)
  const { data: employeesData, isLoading: employeesLoading } = useGetEmployeesQuery(
    {
      search: debouncedEmployeeSearch,
      status: 'active',
      limit: 20
    },
    {
      skip: debouncedEmployeeSearch.trim().length === 0
    }
  );

  // Category options
  const categoryOptions = [
    { value: 'offer', label: 'Offer Letter', color: 'bg-green-100 text-green-800', icon: '📄' },
    { value: 'appointment', label: 'Appointment Letter', color: 'bg-blue-100 text-blue-800', icon: '👔' },
    { value: 'promotion', label: 'Promotion Letter', color: 'bg-purple-100 text-purple-800', icon: '📈' },
    { value: 'relieving', label: 'Relieving Letter', color: 'bg-red-100 text-red-800', icon: '👋' },
    { value: 'experience', label: 'Experience Letter', color: 'bg-orange-100 text-orange-800', icon: '🏆' },
    { value: 'salary_revision', label: 'Salary Revision Letter', color: 'bg-indigo-100 text-indigo-800', icon: '💰' }
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircleSolidIcon className="h-4 w-4 text-green-600" />;
      case 'draft':
        return <ClockSolidIcon className="h-4 w-4 text-yellow-600" />;
      case 'inactive':
        return <XCircleSolidIcon className="h-4 w-4 text-red-600" />;
      default:
        return <ClockSolidIcon className="h-4 w-4 text-gray-600" />;
    }
  };

  const getCategoryBySlug = (category) => {
    const option = categoryOptions.find(opt => opt.value === category);
    return option || { label: category, color: 'bg-gray-100 text-gray-800', icon: '📄' };
  };

  // Filtered data based on UI filters
  const filteredTemplates = useMemo(() => {
    if (!actualTemplatesData?.data) return [];

    return actualTemplatesData.data.filter(template => {
      const matchesStatus = !statusFilter || template.status === statusFilter;
      const matchesCategory = !categoryFilter || template.category === categoryFilter;
      return matchesStatus && matchesCategory;
    });
  }, [actualTemplatesData, statusFilter, categoryFilter]);

  // Filtered and grouped letters data
  const filteredLetters = useMemo(() => {
    if (!actualLettersData?.data) return [];

    return actualLettersData.data.filter(letter => {
      const employeeName = `${letter.employeeId?.firstName || ''} ${letter.employeeId?.lastName || ''}`.toLowerCase();
      const employeeId = (letter.employeeId?.employeeId || '').toLowerCase();
      const searchTerm = lettersSearchQuery.toLowerCase();

      // Only apply filters for non-employees (admins/HR)
      // Employees see all their letters returned by backend
      if (!isEmployee) {
        const matchesSearch = !lettersSearchQuery ||
          employeeName.includes(searchTerm) ||
          employeeId.includes(searchTerm);

        const matchesEmployeeFilter = !lettersEmployeeFilter ||
          employeeId.includes(lettersEmployeeFilter.toLowerCase());

        const matchesCategory = !lettersCategoryFilter || letter.category === lettersCategoryFilter;

        if (!(matchesSearch && matchesEmployeeFilter && matchesCategory)) {
          return false;
        }
      }

      return true;
    });
  }, [actualLettersData, lettersSearchQuery, lettersEmployeeFilter, lettersCategoryFilter, isEmployee]);

  // Group letters by category
  const groupedLetters = useMemo(() => {
    const groups = {};

    filteredLetters.forEach(letter => {
      const category = letter.category || 'uncategorized';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(letter);
    });

    // Sort categories - put popular ones first based on the order we want
    const categoryOrder = [
      'offer', 'appointment', 'promotion', 'experience',
      'relieving', 'salary_revision', 'uncategorized'
    ];

    const sortedGroups = {};
    categoryOrder.forEach(cat => {
      if (groups[cat]) {
        sortedGroups[cat] = groups[cat];
      }
    });

    // Add any remaining categories not in the predefined order
    Object.keys(groups).forEach(cat => {
      if (!sortedGroups[cat]) {
        sortedGroups[cat] = groups[cat];
      }
    });

    return sortedGroups;
  }, [filteredLetters]);

  const handleCloneTemplate = async () => {
    if (!selectedTemplate || !cloneName.trim()) return;

    try {
      await cloneTemplate({ id: selectedTemplate._id, newName: cloneName.trim() }).unwrap();
      toast.success('Template cloned successfully');
      refetchTemplates();
      setShowCloneModal(false);
      setSelectedTemplate(null);
      setCloneName('');
    } catch (error) {
      toast.error(error.data?.message || 'Failed to clone template');
    }
  };

  const handleEditTemplate = (template) => {
    navigate(`/letters/edit/${template._id}`);
  };

  const handlePreviewTemplate = (template) => {
    // Get current date in DD/MM/YYYY format
    const getCurrentDateDDMMYYYY = () => {
      const d = new Date();
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    };

    // Generate sample data matching actual template placeholders
    const sampleMappings = {
      employee_name: 'John Doe',
      employee_code: 'EMP001',
      designation: 'Senior Developer',
      department: 'Engineering',
      joining_date: '15/01/2023',
      promotion_date: '01/10/2024',
      last_working_day: '31/10/2024',
      salary: '85,000',
      company_name: 'TechCorp Solutions Inc.',
      branch: 'Head Office',
      signatory_name: 'Jane Smith',
      signatory_designation: 'HR Manager',
      date: getCurrentDateDDMMYYYY(),
      // Also support dotted notation format
      employee: {
        firstName: 'John',
        lastName: 'Doe',
        employeeId: 'EMP001',
        department: 'Engineering',
        position: 'Senior Developer',
        designation: 'Senior Developer',
        name: 'John Doe'
      },
      company: {
        name: 'TechCorp Solutions Inc.',
        address: '123 Corporate Drive, Business City, ST 12345'
      },
      letterDate: getCurrentDateDDMMYYYY(),
      today: getCurrentDateDDMMYYYY()
    };

    // Replace placeholders in template
    let content = template.htmlContent || '';
    const placeholderRegex = /\{\{([^}]+)\}\}/g;

    content = content.replace(placeholderRegex, (match, placeholder) => {
      const placeholderKey = placeholder.trim();

      // First try exact match
      if (sampleMappings.hasOwnProperty(placeholderKey)) {
        return sampleMappings[placeholderKey];
      }

      // Then try dotted notation (nested objects)
      const keys = placeholderKey.split('.');
      let value = sampleMappings;

      for (const key of keys) {
        value = value?.[key];
        if (value === undefined) break;
      }

      return value !== undefined ? value : match;
    });

    setSelectedTemplate(template);
    setPreviewContent(content);
    setShowPreviewModal(true);
  };

  const handleCloneModal = (template) => {
    setSelectedTemplate(template);
    setCloneName(`${template.name} (Copy)`);
    setShowCloneModal(true);
  };

  const handleGenerateModal = (template) => {
    setSelectedTemplate(template);
    setSelectedEmployee('');
    setLetterPurpose('');
    setLetterNotes('');
    setShowGenerateModal(true);
  };

  const handleLetterActionsModal = (letter) => {
    console.log('📄 Opening actions modal for letter:', letter);
    console.log('📄 Letter _id:', letter?._id);
    setSelectedLetter(letter);
    setShowLetterActionsModal(true);
  };

  const handleChangeTemplateStatus = async (templateId, newStatus) => {
    try {
      await updateLetterTemplate({
        id: templateId,
        templateData: { status: newStatus }
      }).unwrap();
      toast.success(`Template ${newStatus === 'active' ? 'published' : newStatus}!`);
      refetchTemplates();
    } catch (error) {
      toast.error(error.data?.message || 'Failed to update template status');
    }
  };

  const handleGenerateLetter = async () => {
    if (!selectedTemplate || !selectedEmployee) return;

    setGeneratingLetter(true);
    try {
      const letterData = {
        employeeId: selectedEmployee,
        templateId: selectedTemplate._id,
        purpose: letterPurpose.trim() || undefined,
        notes: letterNotes.trim() || undefined
      };

      await generateLetter(letterData).unwrap();
      toast.success('Letter generated successfully!');

      // Refresh the generated letters list
      // Note: We would invalidate the cache, but that would require additional hooks

      // Reset modal state
      setShowGenerateModal(false);
      setSelectedTemplate(null);
      setSelectedEmployee('');
      setEmployeeSearchTerm('');
      setLetterPurpose('');
      setLetterNotes('');
    } catch (error) {
      console.error('Error generating letter:', error);
      toast.error(error?.data?.message || 'Failed to generate letter');
    } finally {
      setGeneratingLetter(false);
    }
  };

  const handleDownloadPDF = async (letterId) => {
    try {
      const pdfUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/letters/${letterId}/download/pdf`;

      // Get token from Redux store
      const state = store.getState();
      const token = state?.auth?.token;

      if (!token) {
        toast.error('Authentication token not found');
        return;
      }

      // Fetch with authentication token
      const response = await fetch(pdfUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          toast.error('Unauthorized access. Please login again.');
          return;
        }
        throw new Error(`Download failed: ${response.status}`);
      }

      // Get filename from response headers or create default
      const contentDisposition = response.headers.get('content-disposition');
      let filename = `letter_${letterId}.pdf`;

      if (contentDisposition) {
        const matches = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (matches && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('Download PDF error:', error);
      toast.error('Failed to download PDF: ' + error.message);
    }
  };



  const handleDeleteLetter = async (letterId) => {
    if (window.confirm('Are you sure you want to delete this letter? This action cannot be undone.')) {
      try {
        await deleteGeneratedLetter(letterId).unwrap();
        toast.success('Letter deleted successfully!');
        setShowLetterActionsModal(false);
        setSelectedLetter(null);
      } catch (error) {
        console.error('Error deleting letter:', error);
        toast.error(error?.data?.message || 'Failed to delete letter');
      }
    }
  };

  const handleSendEmail = async () => {
    try {
      toast.success('Email sent successfully!');
      // In a real implementation, this would send the email
    } catch (error) {
      toast.error('Failed to send email');
    }
  };

  const handlePrintLetter = async (letter) => {
    try {
      // Create a new window for printing
      const printWindow = window.open('', '_blank', 'width=800,height=600');

      if (!printWindow) {
        toast.error('Failed to open print window. Please check your popup blocker.');
        return;
      }

      // Create print content - exact same as view letter modal
      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Letter Print</title>
          <meta charset="UTF-8">
          <style>
            body { margin: 0; padding: 0; }
            .no-print { display: none !important; }
            @media print {
              body { margin: 0; }
              .no-print { display: none !important; }
              *,
              *::before,
              *::after {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
              }
              div,
              span,
              p,
              h1,
              h2,
              h3,
              h4,
              h5,
              h6,
              table,
              th,
              td {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
              }
            }
          </style>
        </head>
        <body>
          <div style="max-height: none; background: white; border: none; padding: 20px; font-size: 14px; line-height: 1.5;">
            <div style="max-width: none;">
              ${letter.htmlContent || 'No letter content available'}
            </div>
          </div>
        </body>
        </html>
      `;

      // Write content to the print window
      printWindow.document.write(printContent);
      printWindow.document.close();

      // Auto-focus the print window
      printWindow.focus();

      // Automatically trigger print dialog after a brief delay to ensure content loads
      printWindow.addEventListener('load', function() {
        setTimeout(() => {
          printWindow.print();
        }, 500); // Small delay to ensure content is fully rendered
      });

      toast.success('Print dialog opened!');
    } catch (error) {
      console.error('Print letter error:', error);
      toast.error('Failed to open print preview');
    }
  };

  const canCreateTemplate = user?.role === 'company_admin' || user?.role === 'super_admin';
  const canEditTemplate = user?.role === 'company_admin' || user?.role === 'super_admin';
  const canGenerateLetters = user?.role === 'hr_admin' || user?.role === 'company_admin' || user?.role === 'super_admin';

  // Debug: Show user info in console
  console.log('🔍 User Info:', user);
  console.log('🎭 User Role:', user?.role);
  console.log('✅ Can Generate Letters:', canGenerateLetters);
  console.log('📝 Sample Templates:', sampleTemplates.length, 'templates');

  return (
    <>
      <Helmet>
        <title>Letter Management - HRMS</title>
      </Helmet>

      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-white/20 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <div className="flex items-center space-x-2 sm:space-x-4 min-w-0 flex-1">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center px-2 sm:px-4 py-2 text-gray-600 hover:text-gray-900 bg-white/50 rounded-lg sm:rounded-xl hover:bg-white/80 transition-all duration-200 group flex-shrink-0"
              >
                <svg className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2 group-hover:-translate-x-1 transition-transform duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.5 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span className="hidden xs:inline">Back</span>
              </button>
              <div className="min-w-0 flex-1">
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent truncate flex items-center">
                  <DocumentTextIcon className="h-6 w-6 sm:h-8 sm:w-8 mr-2 sm:mr-3 text-blue-600" />
                  Letter Management
                </h1>
                <p className="text-xs sm:text-sm text-gray-600 mt-1 hidden sm:block">
                  Manage letter templates and generate official letters
                </p>
              </div>
            </div>
            <div className="flex space-x-2">
              {canCreateTemplate && (
                <Button
                  onClick={() => navigate('/letters/create')}
                  className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add Template
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6">

          {/* Enhanced Tabs - Hide templates tab for employees */}
          {!isEmployee && (
            <div className="bg-white/90 backdrop-blur-sm rounded-xl border border-white/60 shadow-sm mb-6">
              <div className="p-1">
                <div className="flex">
                  <button
                    onClick={() => setActiveTab('templates')}
                    className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-all duration-200 ${
                      activeTab === 'templates'
                        ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg transform scale-105'
                        : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50/50'
                    }`}
                  >
                    📝 Templates ({filteredTemplates?.length || 0})
                  </button>
                  <button
                    onClick={() => setActiveTab('letters')}
                    className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-all duration-200 ${
                      activeTab === 'letters'
                        ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg transform scale-105'
                        : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50/50'
                    }`}
                  >
                    📄 Generated Letters ({lettersData?.stats?.totalLetters || 0})
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Templates Tab */}
          {activeTab === 'templates' && (
            <div className="space-y-6">
              {/* Filters Bar */}
              <div className="bg-white/90 backdrop-blur-sm rounded-xl border border-white/60 p-4 shadow-sm">
                <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
                  <div className="flex-1">
                    <div className="relative">
                      <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Search templates..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 pr-4 py-2 w-full border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 rounded-lg"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">All Status</option>
                      <option value="active">Active</option>
                      <option value="draft">Draft</option>
                      <option value="inactive">Inactive</option>
                    </select>
                    <select
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                      className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">All Categories</option>
                      {categoryOptions.map(category => (
                        <option key={category.value} value={category.value}>
                          {category.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Templates Grid */}
              {templatesLoading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="bg-white/90 rounded-xl border border-white/60 p-6 shadow-sm animate-pulse">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="h-10 w-10 bg-gray-200 rounded-lg"></div>
                        <div className="flex-1">
                          <div className="h-4 bg-gray-200 rounded mb-2"></div>
                          <div className="h-3 bg-gray-200 rounded"></div>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="h-3 bg-gray-200 rounded"></div>
                        <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredTemplates?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredTemplates.map((template) => {
                    const categoryInfo = getCategoryBySlug(template.category);
                    return (
                      <div key={template._id} className="bg-white/90 backdrop-blur-sm rounded-xl border border-white/60 overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                        {/* Card Header */}
                        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="text-2xl">{categoryInfo.icon}</div>
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-white truncate">{template.name}</h3>
                                <div className="flex items-center mt-1">
                                  {getStatusIcon(template.status)}
                                  <span className="text-xs text-blue-100 ml-1 capitalize">{template.status}</span>
                                  {template.isDefault && (
                                    <StarSolidIcon className="h-4 w-4 text-yellow-300 ml-2" title="Default template" />
                                  )}
                                </div>
                              </div>
                            </div>
                            <Menu as="div" className="relative">
                              <Menu.Button className="text-white/80 hover:text-white p-1">
                                <BarsArrowUpIcon className="h-5 w-5" />
                              </Menu.Button>
                              <Menu.Items className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border">
                                <div className="py-1">
                                  <Menu.Item>
                                    {({ active }) => (
                                      <button
                                        onClick={() => handlePreviewTemplate(template)}
                                        className={`flex items-center w-full px-4 py-2 text-sm ${active ? 'bg-gray-50' : ''}`}
                                      >
                                        <EyeIcon className="h-4 w-4 mr-3" />
                                        Preview
                                      </button>
                                    )}
                                  </Menu.Item>
                                  {canEditTemplate && (
                                    <>
                                      <Menu.Item>
                                        {({ active }) => (
                                          <button
                                            onClick={() => handleEditTemplate(template)}
                                            className={`flex items-center w-full px-4 py-2 text-sm ${active ? 'bg-gray-50' : ''}`}
                                          >
                                            <PencilIcon className="h-4 w-4 mr-3" />
                                            Edit
                                          </button>
                                        )}
                                      </Menu.Item>
                                      {template.status !== 'active' && (
                                        <Menu.Item>
                                          {({ active }) => (
                                            <button
                                              onClick={() => handleChangeTemplateStatus(template._id, 'active')}
                                              className={`flex items-center w-full px-4 py-2 text-sm text-green-600 ${active ? 'bg-gray-50' : ''}`}
                                            >
                                              <CheckCircleIcon className="h-4 w-4 mr-3" />
                                              Publish Template
                                            </button>
                                          )}
                                        </Menu.Item>
                                      )}
                                      {template.status === 'active' && (
                                        <Menu.Item>
                                          {({ active }) => (
                                            <button
                                              onClick={() => handleChangeTemplateStatus(template._id, 'inactive')}
                                              className={`flex items-center w-full px-4 py-2 text-sm text-red-600 ${active ? 'bg-gray-50' : ''}`}
                                            >
                                              <XCircleIcon className="h-4 w-4 mr-3" />
                                              Unpublish
                                            </button>
                                          )}
                                        </Menu.Item>
                                      )}
                                      <Menu.Item>
                                        {({ active }) => (
                                          <button
                                            onClick={() => handleCloneModal(template)}
                                            className={`flex items-center w-full px-4 py-2 text-sm ${active ? 'bg-gray-50' : ''}`}
                                          >
                                            <DocumentDuplicateIcon className="h-4 w-4 mr-3" />
                                            Clone
                                          </button>
                                        )}
                                      </Menu.Item>
                                    </>
                                  )}
                                </div>
                              </Menu.Items>
                            </Menu>
                          </div>
                        </div>

                        {/* Card Body */}
                        <div className="p-4 space-y-3">
                          <p className="text-sm text-gray-600 line-clamp-2">
                            {template.description || 'No description available'}
                          </p>

                          {/* Category Badge */}
                          <div className="flex items-center justify-between">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${categoryInfo.color}`}>
                              {categoryInfo.label}
                            </span>
                            <div className="text-xs text-gray-500">
                              Used {template.usageCount || 0} times
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex gap-2 pt-2 border-t">
                            <button
                              onClick={() => handlePreviewTemplate(template)}
                              className="flex-1 py-2 px-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs font-medium text-gray-700 transition-colors flex items-center justify-center"
                            >
                              <EyeIcon className="h-3 w-3 mr-1" />
                              Preview
                            </button>
                            {canGenerateLetters && template.status === 'active' && (
                              <button
                                onClick={() => handleGenerateModal(template)}
                                className="flex-1 py-2 px-2 bg-green-500 hover:bg-green-600 rounded-lg text-xs font-medium text-white transition-colors flex items-center justify-center"
                              >
                                <RocketLaunchIcon className="h-3 w-3 mr-1" />
                                Generate
                              </button>
                            )}
                            {canEditTemplate && (
                              <button
                                onClick={() => handleEditTemplate(template)}
                                className="flex-1 py-2 px-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-xs font-medium text-white transition-colors flex items-center justify-center"
                              >
                                <PencilIcon className="h-3 w-3 mr-1" />
                                Edit
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <DocumentTextSolidIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No templates found</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Get started by creating your first letter template.
                  </p>
                  {canCreateTemplate && (
                    <div className="mt-6">
                      <Button
                        onClick={() => navigate('/letters/create')}
                        className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
                      >
                        <PlusIcon className="h-4 w-4 mr-2" />
                        Create Template
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Letters Tab */}
          {activeTab === 'letters' && (
            <div className="space-y-6">
              {/* Filters Bar for Letters - Hide for employees */}
              {!isEmployee && (
                <div className="bg-white/90 backdrop-blur-sm rounded-xl border border-white/60 p-4 shadow-sm">
                  <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
                    <div className="flex-1">
                      <div className="relative">
                        <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <Input
                          type="text"
                          placeholder="Search by employee name, employee ID..."
                          value={lettersSearchQuery}
                          onChange={(e) => setLettersSearchQuery(e.target.value)}
                          className="pl-10 pr-4 py-2 w-full border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 rounded-lg"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Input
                        type="text"
                        placeholder="Filter by employee ID"
                        value={lettersEmployeeFilter}
                        onChange={(e) => setLettersEmployeeFilter(e.target.value)}
                        className="px-3 py-2 min-w-[150px] border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 rounded-lg text-sm"
                      />
                      <select
                        value={lettersCategoryFilter}
                        onChange={(e) => setLettersCategoryFilter(e.target.value)}
                        className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">All Categories</option>
                        {categoryOptions.map(category => (
                          <option key={category.value} value={category.value}>
                            {category.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {lettersLoading ? (
                <div className="space-y-6">
                  {[1, 2].map((section) => (
                    <div key={section} className="space-y-4">
                      <div className="h-8 bg-gray-200 rounded w-1/4 animate-pulse"></div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="bg-white/90 rounded-xl border border-white/60 p-6 shadow-sm animate-pulse">
                            <div className="space-y-3">
                              <div className="h-4 bg-gray-200 rounded"></div>
                              <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                              <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : Object.keys(groupedLetters).length > 0 ? (
                <div className="space-y-8">
                  {Object.entries(groupedLetters).map(([category, letters]) => {
                    const categoryInfo = getCategoryBySlug(category);
                    return (
                      <div key={category} className="space-y-4">
                        {/* Category Header */}
                        <div className="flex items-center space-x-3 bg-white/80 backdrop-blur-sm rounded-xl border border-white/60 p-4 shadow-sm">
                          <div className="text-3xl">{categoryInfo.icon}</div>
                          <div>
                            <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                              {categoryInfo.label}
                            </h2>
                            <p className="text-sm text-gray-600">{letters.length} letter{letters.length !== 1 ? 's' : ''} generated</p>
                          </div>
                        </div>

                        {/* Letters Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                          {letters.map((letter) => (
                            <div key={letter._id} className="bg-white/90 backdrop-blur-sm rounded-xl border border-white/60 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                              <div className="p-6">
                                <div className="flex items-center mb-4">
                                  <div className="text-2xl mr-3 opacity-75">{categoryInfo.icon}</div>
                                  <div className="flex-1">
                                    <h3 className="font-semibold text-gray-900">
                                      {letter.employeeId?.firstName} {letter.employeeId?.lastName}
                                    </h3>
                                    <p className="text-sm text-gray-500 font-medium">ID: {letter.employeeId?.employeeId}</p>
                                  </div>
                                </div>

                                <div className="space-y-2 mb-4">
                                  <div className="text-xs text-gray-600">
                                    Generated: {(() => {
                                      const d = new Date(letter.issuedDate);
                                      const day = String(d.getDate()).padStart(2, '0');
                                      const month = String(d.getMonth() + 1).padStart(2, '0');
                                      const year = d.getFullYear();
                                      return `${day}/${month}/${year}`;
                                    })()}
                                  </div>
                                  {letter.purpose && (
                                    <div className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-md">
                                      Purpose: {letter.purpose}
                                    </div>
                                  )}
                                </div>

                                <Menu as="div" className="w-full relative">
                                  <Menu.Button className="w-full py-2 px-3 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-sm font-medium transition-colors flex items-center justify-center">
                                    <BarsArrowUpIcon className="h-4 w-4 mr-1" />
                                    Actions
                                  </Menu.Button>
                                  <Menu.Items className="absolute left-0 bottom-full mb-2 w-full bg-white rounded-lg shadow-lg border z-50">
                                    <div className="py-1">
                                      <Menu.Item>
                                        {({ active }) => (
                                          <button
                                            onClick={() => handleLetterActionsModal(letter)}
                                            className={`flex items-center w-full px-4 py-2 text-sm ${active ? 'bg-gray-50' : ''}`}
                                          >
                                            <EyeIcon className="h-4 w-4 mr-3" />
                                            View Letter
                                          </button>
                                        )}
                                      </Menu.Item>
                                      <Menu.Item>
                                        {({ active }) => (
                                          <button
                                            onClick={() => handleDownloadPDF(letter._id)}
                                            className={`flex items-center w-full px-4 py-2 text-sm ${active ? 'bg-gray-50' : ''}`}
                                          >
                                            <DocumentTextIcon className="h-4 w-4 mr-3" />
                                            Download PDF
                                          </button>
                                        )}
                                      </Menu.Item>

                                      {canGenerateLetters && (
                                        <Menu.Item>
                                          {({ active }) => (
                                            <button
                                              onClick={() => handleSendEmail(letter)}
                                              className={`flex items-center w-full px-4 py-2 text-sm ${active ? 'bg-gray-50' : ''}`}
                                            >
                                              <ShareIcon className="h-4 w-4 mr-3" />
                                              Send via Email
                                            </button>
                                          )}
                                        </Menu.Item>
                                      )}
                                      <Menu.Item>
                                        {({ active }) => (
                                          <button
                                            onClick={() => handlePrintLetter(letter)}
                                            className={`flex items-center w-full px-4 py-2 text-sm ${active ? 'bg-gray-50' : ''}`}
                                          >
                                            <PrinterIcon className="h-4 w-4 mr-3" />
                                            Print Letter
                                          </button>
                                        )}
                                      </Menu.Item>
                                      {user?.role !== 'employee' && (
                                        <Menu.Item>
                                          {({ active }) => (
                                            <button
                                              onClick={() => handleDeleteLetter(letter._id)}
                                              className={`flex items-center w-full px-4 py-2 text-sm text-red-600 ${active ? 'bg-red-50' : ''}`}
                                            >
                                              <TrashIcon className="h-4 w-4 mr-3" />
                                              Delete Letter
                                            </button>
                                          )}
                                        </Menu.Item>
                                      )}
                                    </div>
                                  </Menu.Items>
                                </Menu>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No generated letters</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Letters will appear here after they are generated.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Clone Modal */}
      <Modal
        isOpen={showCloneModal}
        onClose={() => {
          setShowCloneModal(false);
          setSelectedTemplate(null);
          setCloneName('');
        }}
        title="Clone Template"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New Template Name
            </label>
            <Input
              type="text"
              value={cloneName}
              onChange={(e) => setCloneName(e.target.value)}
              placeholder="Enter template name"
              className="w-full"
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button
              onClick={() => {
                setShowCloneModal(false);
                setSelectedTemplate(null);
                setCloneName('');
              }}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCloneTemplate}
              disabled={!cloneName.trim()}
              className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
            >
              Clone Template
            </Button>
          </div>
        </div>
      </Modal>

      {/* Preview Modal */}
      {showPreviewModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4 text-center sm:p-0">
            <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm transition-opacity" onClick={() => {
              setShowPreviewModal(false);
              setSelectedTemplate(null);
              setPreviewContent('');
            }} />

            <div className="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl transition-all w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl">
                    <MagnifyingGlassPlusIcon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Template Preview</h2>
                    <p className="text-sm text-gray-600">{selectedTemplate?.name}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowPreviewModal(false);
                    setSelectedTemplate(null);
                    setPreviewContent('');
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6">
                {/* Sample Data Notice */}
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start space-x-2">
                    <EyeIcon className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-blue-900">Preview Mode</p>
                      <p className="text-xs text-blue-700">
                        This preview shows sample data filled in the template. Placeholders like {'{{employee.firstName}}'} have been replaced with demo values for illustration.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Preview Content */}
                <div className="max-h-96 overflow-y-auto bg-gray-50 border border-gray-200 rounded-lg p-6">
                  {previewContent ? (
                    <div
                      className="prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: previewContent }}
                    />
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <DocumentTextIcon className="mx-auto h-8 w-8 text-gray-400" />
                      <p className="mt-2 text-sm">No content to preview</p>
                    </div>
                  )}
                </div>

                {/* Modal Actions */}
                <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
                  <Button
                    onClick={() => {
                      setShowPreviewModal(false);
                      setSelectedTemplate(null);
                      setPreviewContent('');
                    }}
                    variant="outline"
                  >
                    Close Preview
                  </Button>
                  {canEditTemplate && (
                    <Button
                      onClick={() => {
                        setShowPreviewModal(false);
                        if (selectedTemplate) handleEditTemplate(selectedTemplate);
                        setSelectedTemplate(null);
                        setPreviewContent('');
                      }}
                      className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
                    >
                      <PencilIcon className="w-4 h-4 mr-2" />
                      Edit Template
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Generate Letter Modal */}
      <Modal
        isOpen={showGenerateModal}
        onClose={() => {
          if (!generatingLetter) {
            setShowGenerateModal(false);
            setSelectedTemplate(null);
            setSelectedEmployee('');
            setEmployeeSearchTerm('');
            setLetterPurpose('');
            setLetterNotes('');
          }
        }}
        title={`Generate ${selectedTemplate?.name || 'Letter'}`}
      >
        <div className="space-y-4">
          {/* Employee Search and Selection - following asset allocation pattern */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search & Select Employee <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2">
              <Input
                type="text"
                placeholder="Search employees (minimum 2 characters)..."
                value={employeeSearchTerm}
                onChange={(e) => setEmployeeSearchTerm(e.target.value)}
                className="w-full"
                disabled={generatingLetter}
              />
              <select
                value={selectedEmployee}
                onChange={(e) => setSelectedEmployee(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-200 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={generatingLetter}
              >
                <option value="">
                  {employeesLoading
                    ? 'Loading employees...'
                    : debouncedEmployeeSearch
                      ? 'Select an employee from search results'
                      : 'Start typing to search employees'
                  }
                </option>
                {employeesData?.data?.employees?.map((employee) => (
                  <option key={employee._id} value={employee._id}>
                    {employee.firstName} {employee.lastName} ({employee.employeeId})
                  </option>
                ))}
              </select>
              {debouncedEmployeeSearch && employeesData?.data?.employees?.length === 0 && !employeesLoading && (
                <p className="text-sm text-gray-500">No employees found matching your search.</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Letter Purpose
            </label>
            <Input
              type="text"
              value={letterPurpose}
              onChange={(e) => setLetterPurpose(e.target.value)}
              placeholder="e.g., New Hire, Promotion, Salary Revision..."
              className="w-full"
              disabled={generatingLetter}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional Notes (Optional)
            </label>
            <textarea
              value={letterNotes}
              onChange={(e) => setLetterNotes(e.target.value)}
              rows={3}
              placeholder="Any additional notes or requirements..."
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              disabled={generatingLetter}
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <DocumentTextIcon className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-blue-900">Letter Generation</p>
                <p className="text-xs text-blue-700">
                  This will create a personalized letter for the selected employee using the template: <strong>{selectedTemplate?.name}</strong>
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              onClick={() => {
                if (!generatingLetter) {
                  setShowGenerateModal(false);
                  setSelectedTemplate(null);
                  setSelectedEmployee('');
                  setEmployeeSearchTerm('');
                  setLetterPurpose('');
                  setLetterNotes('');
                }
              }}
              variant="outline"
              disabled={generatingLetter}
            >
              Cancel
            </Button>
            <Button
              onClick={handleGenerateLetter}
              disabled={!selectedEmployee || generatingLetter}
              className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {generatingLetter ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Generating...
                </>
              ) : (
                <>
                  <RocketLaunchIcon className="w-4 h-4 mr-2" />
                  Generate Letter
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Letter Actions Modal */}
      <Modal
        isOpen={showLetterActionsModal}
        onClose={() => {
          setShowLetterActionsModal(false);
          setSelectedLetter(null);
        }}
        title={`Letter: ${selectedLetter?.employeeId?.firstName} ${selectedLetter?.employeeId?.lastName}`}
      >
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="font-medium text-gray-900">Employee</p>
                <p className="text-gray-600">{selectedLetter?.employeeId?.firstName} {selectedLetter?.employeeId?.lastName}</p>
              </div>
              <div>
                <p className="font-medium text-gray-900">Employee ID</p>
                <p className="text-gray-600">{selectedLetter?.employeeId?.employeeId}</p>
              </div>
              <div>
                <p className="font-medium text-gray-900">Generated On</p>
                <p className="text-gray-600">
                  {selectedLetter ? (() => {
                    const d = new Date(selectedLetter.issuedDate);
                    const day = String(d.getDate()).padStart(2, '0');
                    const month = String(d.getMonth() + 1).padStart(2, '0');
                    const year = d.getFullYear();
                    return `${day}/${month}/${year}`;
                  })() : ''}
                </p>
              </div>
              <div>
                <p className="font-medium text-gray-900">Type</p>
                <p className="text-gray-600 capitalize">{selectedLetter?.category?.replace('_', ' ')}</p>
              </div>
            </div>
          </div>

          {/* Letter Content Section */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center mb-3">
              <EyeIcon className="h-5 w-5 text-gray-500 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">Letter Content</h3>
            </div>
            <div className="max-h-96 overflow-y-auto bg-gray-50 border border-gray-200 rounded-lg p-4">
              {selectedLetter?.htmlContent ? (
                <div
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: selectedLetter.htmlContent }}
                  style={{ fontSize: '14px', lineHeight: '1.5' }}
                />
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <DocumentTextIcon className="mx-auto h-8 w-8 text-gray-400" />
                  <p className="mt-2 text-sm">No letter content available</p>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <button
              onClick={() => selectedLetter && handleDownloadPDF(selectedLetter._id)}
              disabled={!selectedLetter}
              className="flex items-center justify-center px-4 py-3 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <DocumentTextIcon className="w-5 h-5 mr-2" />
              Download PDF
            </button>

            {canGenerateLetters && (
              <button
                onClick={() => handleSendEmail()}
                className="flex items-center justify-center px-4 py-3 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg text-sm font-medium transition-colors"
              >
                <ShareIcon className="w-5 h-5 mr-2" />
                Send via Email
              </button>
            )}

            <button
              onClick={() => {
                // The modal already shows the letter details, this is just for emphasis
                toast.success('Letter content is displayed above');
              }}
              className={`flex items-center justify-center px-4 py-3 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-lg text-sm font-medium transition-colors ${canGenerateLetters ? 'md:col-span-1' : 'md:col-span-2'}`}
            >
              <EyeIcon className="w-5 h-5 mr-2" />
              View Full Letter
            </button>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              onClick={() => {
                setShowLetterActionsModal(false);
                setSelectedLetter(null);
              }}
              variant="outline"
            >
              Close
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default LetterManagement;
